/**
 * What the Equipment step's chips and search show (`equipment-page-stage`, spec R7 and R10).
 *
 * Pure: it reads the register rows and the ONE identify model the step builds, and decides which
 * rows the list shows and which chips the left panel offers. It never re-derives a row's state —
 * that is `buildIdentifyModel`'s alone — so a chip's count and the rows it shows cannot disagree.
 */
import { ink, semantic, statusRoleOnStep } from '@repo/design-tokens';
import type { StatusRole } from '@repo/design-tokens';
import type { FilterChip, TagTone } from '@repo/design-system';
import {
  IDENTITY_STATE_LABELS,
  PLAN_STATE_LABELS,
  isGenericCatalogueManufacturer,
  registerRowName,
  type AssetIdentityState,
  type AssetPlanState,
  type IdentifyGroup,
  type IdentifyModel,
} from '@repo/front-end-utils';

/**
 * One chip: an identity state, a plan state, or every row. Two more come with
 * `equipment-page-adoption`: `doesnt_fit`, the *Doesn't fit* chip (R9), and `needs_a_look`, an
 * adoption import's landing (R11) — a filter no chip offers, so no chip is active while it holds.
 */
export type EquipmentChip =
  | AssetIdentityState
  | AssetPlanState
  | 'doesnt_fit'
  | 'needs_a_look'
  | 'all';

/** The fields the search reads. Structural, so a `TwinAssetResponse` satisfies it. */
export interface EquipmentFilterRow {
  id: number;
  name: string;
  asset_name?: string | null;
  customer_asset_identifier?: string | null;
  source_asset_id?: string | null;
  manufacturer_raw?: string | null;
  model_raw?: string | null;
  product_line_raw?: string | null;
  category_raw?: string | null;
  /** The fit judge's answer and a person's Keep (`equipment-page-adoption`, R9). */
  doesnt_fit?: boolean | null;
  doesnt_fit_kept?: boolean;
}

/** A row the *Doesn't fit* chip holds: flagged and not kept — as `counts.doesntFit` counts it. */
const unkeptDoesntFit = (row: EquipmentFilterRow): boolean =>
  row.doesnt_fit === true && row.doesnt_fit_kept !== true;

/** *Needs a look*'s three: To identify, To confirm, and an unkept *Doesn't fit* (R11). */
const needsALook = (row: EquipmentFilterRow, model: Pick<IdentifyModel, 'stateOf'>): boolean => {
  const state = model.stateOf(row.id);
  return state === 'to_identify' || state === 'to_confirm' || unkeptDoesntFit(row);
};

const IDENTITY_CHIPS: ReadonlySet<EquipmentChip> = new Set<EquipmentChip>([
  'brand',
  'to_confirm',
  'generic',
  'not_tracked',
  'to_identify',
]);

/**
 * What the routes board's search covers (§9): "name, both identifiers and the customer's
 * description". The displayed name and the customer's name both, because an operator may have
 * either to hand.
 */
const searchedText = (row: EquipmentFilterRow): string =>
  [
    registerRowName(row),
    row.name,
    row.customer_asset_identifier,
    row.source_asset_id,
    row.manufacturer_raw,
    row.model_raw,
    row.product_line_raw,
    row.category_raw,
  ]
    .filter((part): part is string => typeof part === 'string' && part !== '')
    .join('\n')
    .toLowerCase();

/** The ids the list shows: the chip AND the query. */
export const equipmentRowFilter = (
  rows: readonly EquipmentFilterRow[],
  model: Pick<IdentifyModel, 'stateOf' | 'planStateOf'>,
  { chip, query }: { chip: EquipmentChip; query: string },
): ReadonlySet<number> => {
  const q = query.trim().toLowerCase();
  const visible = new Set<number>();
  // Which of the model's two answers the chip asks, decided once rather than per row.
  const own = chip === 'doesnt_fit' || chip === 'needs_a_look';
  const stateOf =
    chip === 'all' || own ? null : IDENTITY_CHIPS.has(chip) ? model.stateOf : model.planStateOf;
  for (const row of rows) {
    if (stateOf && stateOf(row.id) !== chip) continue;
    if (chip === 'doesnt_fit' && !unkeptDoesntFit(row)) continue;
    if (chip === 'needs_a_look' && !needsALook(row, model)) continue;
    if (q && !searchedText(row).includes(q)) continue;
    visible.add(row.id);
  }
  return visible;
};

/** The label the main panel's title uses for a chip. */
export const equipmentChipLabel = (chip: EquipmentChip): string => {
  if (chip === 'all') return 'All assets';
  if (chip === 'doesnt_fit') return 'Doesn’t fit';
  if (chip === 'needs_a_look') return 'Needs a look';
  return IDENTITY_CHIPS.has(chip)
    ? IDENTITY_STATE_LABELS[chip as AssetIdentityState]
    : PLAN_STATE_LABELS[chip as AssetPlanState];
};

export interface EquipmentChipGroups {
  todo: FilterChip<EquipmentChip>[];
  done: FilterChip<EquipmentChip>[];
  plan: FilterChip<EquipmentChip>[];
}

/**
 * The three groups frame 04 draws — *To do*, *Done*, *Plan* — each chip's dot its state's status
 * role (`EQUIPMENT_STATE_ROLE`); a done chip has no dot.
 *
 * *Keep off plan* is offered only while a row carries it; when the plan did not load, the plan
 * group says so in one chip rather than calling every row *Not on plan* on evidence never read.
 */
export const equipmentChipGroups = (
  model: Pick<IdentifyModel, 'counts' | 'planKnown'>,
  active: EquipmentChip,
): EquipmentChipGroups => {
  const { byState, byPlanState, total, doesntFit } = model.counts;
  const chip = (key: EquipmentChip, count: number): FilterChip<EquipmentChip> => ({
    key,
    label: key === 'all' ? 'All' : equipmentChipLabel(key),
    count,
    dot: key === 'all' ? ink[3] : equipmentChipDot(key),
    active: active === key,
  });
  const plan: FilterChip<EquipmentChip>[] = [chip('all', total)];
  if (model.planKnown) {
    plan.push(chip('not_on_plan', byPlanState.not_on_plan));
    plan.push(chip('on_plan', byPlanState.on_plan));
    if (byPlanState.keep_off_plan > 0) {
      plan.push(chip('keep_off_plan', byPlanState.keep_off_plan));
    }
  } else {
    plan.push(chip('plan_not_loaded', byPlanState.plan_not_loaded));
  }
  return {
    todo: [
      chip('to_identify', byState.to_identify),
      chip('to_confirm', byState.to_confirm),
      // After To confirm, only while a row is flagged and unkept (`equipment-page-adoption`, R9).
      ...(doesntFit > 0 ? [chip('doesnt_fit', doesntFit)] : []),
    ],
    done: [
      chip('brand', byState.brand),
      chip('generic', byState.generic),
      chip('not_tracked', byState.not_tracked),
    ],
    plan,
  };
};

/** Whether `chip` is one of the chips `groups` offers — the active chip falls back to *All* when not. */
export const isChipOffered = (groups: EquipmentChipGroups, chip: EquipmentChip): boolean =>
  [...groups.todo, ...groups.done, ...groups.plan].some(c => c.key === chip);

/**
 * The main panel's count line (spec R8): *214 assets*, *1 asset*, or, while the search has text,
 * *1 of 2 assets match "tread"* — where the second number is what the chip alone would show.
 */
export const equipmentCountText = (visible: number, ofChip: number, query: string): string => {
  const q = query.trim();
  if (!q) return assetCount(visible);
  return `${visible} of ${assetCount(ofChip)} match “${q}”`;
};

/**
 * What follows the count line on *Needs a look* (`equipment-page-adoption`, R11): the three kinds it
 * holds. Empty for every chip.
 */
export const equipmentChipCaption = (chip: EquipmentChip): string =>
  chip === 'needs_a_look' ? ' · Doesn’t fit, To confirm and To identify' : '';

/**
 * The polite region's sentence for *Needs a look* (R11): *Needs a look: 12 assets.* Null for every
 * chip, whose region says the title and the count line.
 */
export const equipmentAnnouncement = (chip: EquipmentChip, visible: number): string | null =>
  chip === 'needs_a_look' ? `Needs a look: ${assetCount(visible)}.` : null;

/** *1 asset*, *214 assets* — the step's one way of counting rows. */
export const assetCount = (n: number): string => `${n} ${n === 1 ? 'asset' : 'assets'}`;

/**
 * The Rows view's head while *From this import* stands (`equipment-page-reimport`, R11): *From
 * this import* · *{n} assets · added first*, announced as *{n} assets from this import, added
 * first*.
 */
export const FROM_IMPORT_TITLE = 'From this import';
export const fromImportCountText = (n: number): string => `${assetCount(n)} · added first`;
export const fromImportAnnouncement = (n: number): string =>
  `${assetCount(n)} from this import, added first`;

/** How many rows a chip holds before any search — the "of N" in the count line. */
export const equipmentChipCount = (
  model: Pick<IdentifyModel, 'counts'>,
  chip: EquipmentChip,
): number => {
  if (chip === 'all') return model.counts.total;
  if (chip === 'doesnt_fit') return model.counts.doesntFit;
  /*
   * The three are disjoint: the server judges fit only on a row with a catalogue product
   * (`doesnt_fit` is false without one), and To identify and To confirm rows have none.
   */
  if (chip === 'needs_a_look') {
    const { to_identify: toIdentify, to_confirm: toConfirm } = model.counts.byState;
    return toIdentify + toConfirm + model.counts.doesntFit;
  }
  return IDENTITY_CHIPS.has(chip)
    ? model.counts.byState[chip as AssetIdentityState]
    : model.counts.byPlanState[chip as AssetPlanState];
};

/** Every state the step draws a chip or a `Tag` for. */
export type EquipmentStateKey = AssetIdentityState | AssetPlanState | 'doesnt_fit' | 'needs_a_look';

/**
 * Each state's status role on this step (Rob's status page, *Onboarding Entity Statuses*;
 * `onboarding-status-colours-equipment`, R1), so the chip dot and the `Tag` that repeats its count
 * are both read from one place and no hue is chosen here. Identifying and confirming are this
 * step's work. Placing is Floor Plan's, so on Equipment *Not on plan* is a problem to fix elsewhere
 * (the status page's one step exception; the wizard's step ids as plain strings, as
 * `OnboardingList/rowState.tsx` writes the mirror case). *Doesn't fit* asks a person to fix the
 * product. Done states (`'done'`) are unmarked: their words stay, with no dot and no tint.
 */
export const EQUIPMENT_STATE_ROLE: Record<EquipmentStateKey, StatusRole | 'done'> = {
  to_identify: 'workToDo',
  to_confirm: 'workToDo',
  doesnt_fit: 'problem',
  not_on_plan: statusRoleOnStep('workToDo', 'plan', 'assets'),
  // A landing filter no chip offers, holding To identify, To confirm and Doesn't fit.
  needs_a_look: 'workToDo',
  not_tracked: 'neutral',
  keep_off_plan: 'neutral',
  plan_not_loaded: 'neutral',
  brand: 'done',
  generic: 'done',
  on_plan: 'done',
};

/** A state's `Tag`: its tone, and whether it carries the dot. */
export interface EquipmentTagLook {
  tone: TagTone;
  dot: boolean;
}

/** A role every state on this step names: one whose `Tag` tone exists (`tagTone` is never null). */
type TaggedRole = Exclude<StatusRole, 'deadEnd'>;

const UNMARKED: EquipmentTagLook = { tone: 'neutral', dot: false };

// Own keys only, so a state cast from data that names an inherited key (`constructor`) is uncovered.
const roleOf = (state: string): TaggedRole | 'done' | undefined =>
  Object.prototype.hasOwnProperty.call(EQUIPMENT_STATE_ROLE, state)
    ? (EQUIPMENT_STATE_ROLE[state as EquipmentStateKey] as TaggedRole | 'done')
    : undefined;

/**
 * The look a state's `Tag` wears wherever the step shows it (R2): the role's tone with its dot, or
 * the neutral tone with no dot for a done state. A state the table does not cover (a value cast
 * from data) is drawn unmarked rather than throwing during render.
 */
export const equipmentTagLook = (state: string): EquipmentTagLook => {
  const role = roleOf(state);
  if (!role || role === 'done') return UNMARKED;
  return { tone: semantic.status.roles[role].tagTone, dot: true };
};

/** A state's chip dot: its role's mark, or none for a done state. */
const equipmentChipDot = (state: string): string | undefined => {
  const role = roleOf(state);
  return !role || role === 'done' ? undefined : semantic.status.roles[role].mark;
};

/** Where the step lands: a chip and a view (`equipment-page-landed`, R6). */
export interface EquipmentLanding {
  chip: EquipmentChip;
  view: 'rows' | 'groups';
}

/**
 * Where the Equipment step opens — on arriving, and after an upload lands (R6, R7).
 *
 * Frame 04's note: "The page opens on To identify when anything is left, otherwise on All", and
 * "Groups view is the default while anything is to do". *To confirm* is the other half of *To do*,
 * so a register with nothing to identify but guesses to confirm opens on those rather than on *All*
 * (a reading recorded for Rob to ratify).
 *
 * An ADOPTION import (`mode === 'adopt'`, `equipment-page-adoption`, R11) lands on *Needs a look*
 * in Rows instead, while any row is To identify, To confirm or an unkept *Doesn't fit*.
 */
export const equipmentLanding = (
  model: Pick<IdentifyModel, 'counts'>,
  mode?: 'customer' | 'adopt' | null,
): EquipmentLanding => {
  const { to_identify: toIdentify, to_confirm: toConfirm } = model.counts.byState;
  if (mode === 'adopt' && toIdentify + toConfirm + model.counts.doesntFit > 0) {
    return { chip: 'needs_a_look', view: 'rows' };
  }
  const chip: EquipmentChip = toIdentify > 0 ? 'to_identify' : toConfirm > 0 ? 'to_confirm' : 'all';
  return { chip, view: toIdentify + toConfirm > 0 ? 'groups' : 'rows' };
};

type GroupProposal = NonNullable<IdentifyGroup['proposal']>;

/**
 * A guess's maker (`equipment-page-confirm`, R1): *Hammer Strength*, or *Generic* for the
 * catalogue's generic bucket; the product line where no manufacturer is named.
 */
export const guessMaker = (proposal: GroupProposal): string | null =>
  isGenericCatalogueManufacturer(proposal.manufacturer?.trim())
    ? 'Generic'
    : proposal.manufacturer?.trim() || proposal.product_line?.trim() || null;

/** *Hammer Strength · Iso-Lateral Chest Press* — a group's guess as its header shows it (R1). */
export const guessText = (proposal: GroupProposal): string =>
  [guessMaker(proposal), proposal.product].filter(Boolean).join(' · ');
